/**
 * Shared de-duplication for profile questions sourced from
 * `question_catalog`.
 *
 * The profile wizard (`useProfileQuestionsQuery` in `ProfileWizardContext`) and
 * the completion-percentage hook (`useProfileProgress`) MUST agree on the set of
 * questions a user is actually asked. If they diverge — e.g. the wizard hides a
 * question via de-dup but the progress denominator still counts it — completion
 * can never reach 100% (REK-1869: several distinct questions share a
 * `profile_attribute_key`, so the wizard shows one and hides the rest, while the
 * progress bar counted them all). Both call the helpers here so the "questions
 * asked" set is computed identically in one place.
 *
 * De-dup rules (order-sensitive — pass rows in the wizard's display order, i.e.
 * `priority_score DESC, id ASC`. The `id` tie-breaker is required: without it,
 * tied priority scores leave row order unspecified, so the surviving row per
 * key could differ between the wizard query and the progress query — which
 * would diverge "shown" from "counted" and stall completion):
 *   1. By `profile_attribute_key` — keep the first row for each non-empty key.
 *   2. By a key generated from the question text — for rows WITHOUT a
 *      `profile_attribute_key`.
 *   3. By normalized (lowercased, trimmed) question text — applied to every row.
 *
 * Equivalence fills (flag-on, `surveys.equivalent_answers`): `fillEquivalentAnswers`
 * marks a question answered when the member holds ANY equivalent — DOB (survey
 * or flat `date_of_birth`) satisfies the age-range question, flat
 * `postal_code` (or the Blue zip row) satisfies the zip question — so the
 * wizard never re-asks them and progress counts them. Both surfaces apply the
 * same fill, so "shown" == "counted" holds under the flag too.
 */

import {
  AGE_RANGE_QUESTION_ID,
  getEffectiveAnswer,
  isQuestionSatisfied,
  type AgeRangeOptions,
  type EquivalenceCatalog,
  type EquivalenceSnapshot,
} from './questionEquivalence';

export interface DedupableQuestion {
  profile_attribute_key?: string | null;
  question_text?: string | null;
}

/**
 * Generate a stable key from a question's text (prefix-stripped, punctuation
 * removed, spaced->underscored, capped at 50 chars). Mirrors the wizard's
 * field-key generation so both surfaces collapse the same near-duplicates.
 */
export function generateProfileQuestionKey(questionText: string): string {
  const key = questionText
    .toLowerCase()
    .replace(/^(what is your |what's your |what are your |do you |are you |have you )/i, '')
    .replace(/[?!.,;:'"()[\]{}]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 50);

  return key || 'unknown_question';
}

/**
 * Return only the questions the wizard would actually present, in input order.
 * Pass rows ordered by `priority_score DESC` to match the wizard exactly.
 */
export function dedupeProfileQuestions<T extends DedupableQuestion>(questions: T[]): T[] {
  const seenAttributeKeys = new Set<string>();
  const seenQuestionTexts = new Set<string>();
  const seenGeneratedKeys = new Set<string>();

  return questions.filter((q) => {
    if (q.profile_attribute_key) {
      if (seenAttributeKeys.has(q.profile_attribute_key)) {
        return false;
      }
      seenAttributeKeys.add(q.profile_attribute_key);
    } else {
      const generatedKey = generateProfileQuestionKey(q.question_text || '');
      if (seenGeneratedKeys.has(generatedKey)) {
        return false;
      }
      seenGeneratedKeys.add(generatedKey);
    }

    const normalizedText = q.question_text?.toLowerCase().trim();
    if (normalizedText && seenQuestionTexts.has(normalizedText)) {
      return false;
    }
    if (normalizedText) {
      seenQuestionTexts.add(normalizedText);
    }

    return true;
  });
}

/**
 * Equivalence fills for the profile wizard + progress (flag-on path only —
 * callers gate on `surveys.equivalent_answers` and pass a snapshot built
 * from shape-aware, non-empty answers).
 *
 * For every question id WITHOUT a direct answer that the snapshot satisfies
 * via ANY equivalent, returns the effective value to expose (group sibling
 * answer, flat-key value, or — directionally — the DOB-derived age-range
 * option label). Callers merge the result into their answer map so
 * `isFieldAnswered` / gating / progress counting treat the question as
 * answered without re-asking it. Questions satisfied with no exposable value
 * (e.g. profile-email suppression, or a DOB that maps to no configured age
 * option) are omitted — the caller keeps asking those, matching the server.
 *
 * `ageRangeOptions` carries the configured age-range options (labels or
 * `{ id, text }` pairs — pairs resolve the derivation to the stored-form
 * option id); without it the age question is never filled (a raw DOB stored
 * as the age answer would break every age-gated dependency).
 */
export function fillEquivalentAnswers(
  questionIds: readonly string[],
  snapshot: EquivalenceSnapshot,
  answersById: ReadonlyMap<string, unknown>,
  flatAnswersByKey: ReadonlyMap<string, unknown>,
  ageRangeOptions?: AgeRangeOptions,
  /**
   * Catalog slice for generic `profile_attribute_key` matching (contract
   * v3 `matchBy`, flag-on path): unlisted duplicate ids sharing a held
   * fact fill too. Omit for explicit-groups-only fills.
   */
  equivalenceCatalog?: EquivalenceCatalog | null,
): Map<string, unknown> {
  const filled = new Map<string, unknown>();
  // Generic duplicates live outside the probe universe below (the caller
  // passes rendered field ids, and an unlisted twin is never among them),
  // so probe the catalog ids too — the twin fills under its own id.
  const probeIds = equivalenceCatalog
    ? [...new Set([...questionIds, ...equivalenceCatalog.questionProfileKeys.keys()])]
    : questionIds;
  for (const id of probeIds) {
    if (snapshot.answeredIds.has(id)) continue;
    if (!isQuestionSatisfied(id, snapshot, equivalenceCatalog ?? null)) continue;
    const effective =
      id === AGE_RANGE_QUESTION_ID
        ? getEffectiveAnswer(id, answersById, flatAnswersByKey, ageRangeOptions, equivalenceCatalog ?? null)
        : getEffectiveAnswer(id, answersById, flatAnswersByKey, undefined, equivalenceCatalog ?? null);
    if (effective !== undefined) filled.set(id, effective);
  }
  return filled;
}
